import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, CheckCircle2, Loader2, Banknote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDkk = (n) => `${Number(n || 0).toLocaleString("da-DK", { maximumFractionDigits: 2 })} kr`;
const DK_MONTHS = ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];

export default function PayoutsAdminTab({ token, partners = [] }) {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  const [payouts, setPayouts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);

  const loadPayouts = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pickup-payouts`, { headers: { Authorization: `Bearer ${token}` } });
      setPayouts(data.payouts || []);
    } finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { loadPayouts(); }, [loadPayouts]);

  const downloadPdf = async (p) => {
    setBusyId(p.id);
    try {
      const res = await axios.get(`${API}/admin/pickup-points/${p.id}/statement.pdf?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `EMA-Opgoerelse-${(p.shop_name || "shop").replace(/[^a-z0-9]+/gi, "-")}-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Kunne ikke hente PDF"); }
    finally { setBusyId(null); }
  };

  const markPaid = async (p) => {
    const ref = prompt("Bank-reference / Mobilepay-ID (valgfrit):");
    setBusyId(p.id);
    try {
      const { data } = await axios.post(`${API}/admin/pickup-points/${p.id}/statements/${year}-${month}/mark-paid`,
        { paid: true, payout_ref: ref || null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Markeret betalt: ${fmtDkk(data.amount_dkk)} til ${p.shop_name}`);
      await loadPayouts();
    } catch { toast.error("Fejl"); }
    finally { setBusyId(null); }
  };

  const period = `${year}-${String(month).padStart(2, "0")}`;
  const paidThisPeriod = payouts.filter((x) => x.period === period && x.paid);
  const paidIds = new Set(paidThisPeriod.map((x) => x.partner_id));
  const totalPaid = paidThisPeriod.reduce((s, x) => s + Number(x.amount_dkk || 0), 0);

  return (
    <div className="space-y-5" data-testid="payouts-admin">
      <p className="text-sm text-brand-text2">Generér og markér månedlige udbetalinger til pickup-point partnere. Vælg periode for at se kommissions-beløb.</p>

      <div className="flex items-center gap-3 flex-wrap">
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="input-flat" data-testid="payout-month">
          {DK_MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="input-flat" data-testid="payout-year">
          {[now.getFullYear() - 1, now.getFullYear()].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex-1" />
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Udbetalt {period}</div>
          <div className="font-display font-bold text-2xl text-emerald-700">{fmtDkk(totalPaid)}</div>
        </div>
      </div>

      <div className="bg-white border border-brand">
        <table className="w-full text-sm">
          <thead className="bg-brand-bg text-brand-text2 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Partner</th>
              <th className="text-left p-3">Udbetalingsmetode</th>
              <th className="text-left p-3">Status {period}</th>
              <th className="text-right p-3">Handling</th>
            </tr>
          </thead>
          <tbody>
            {partners.filter(p => p.active).length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-brand-text2">Ingen aktive partnere</td></tr>
            )}
            {partners.filter(p => p.active).map((p) => {
              const isPaid = paidIds.has(p.id);
              const paidRecord = paidThisPeriod.find((x) => x.partner_id === p.id);
              return (
                <tr key={p.id} className="border-t border-brand" data-testid={`payout-row-${p.id}`}>
                  <td className="p-3">
                    <div className="font-semibold text-brand-dark">{p.shop_name}</div>
                    <div className="text-xs text-brand-text2">{p.shop_city}</div>
                  </td>
                  <td className="p-3 text-xs text-brand-text2">
                    {p.payout_method === "mobilepay" ? `Mobilepay ${p.mobilepay_number || ""}` :
                     p.payout_iban ? `IBAN ${p.payout_iban}` : <span className="text-amber-700">Ikke konfigureret</span>}
                  </td>
                  <td className="p-3">
                    {isPaid ? (
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                        ✓ Betalt {fmtDkk(paidRecord?.amount_dkk)}
                      </span>
                    ) : (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Ikke udbetalt</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => downloadPdf(p)} disabled={busyId === p.id}
                              className="text-xs px-2 py-1 border border-brand hover:bg-brand-bg flex items-center gap-1"
                              data-testid={`payout-pdf-${p.id}`}>
                        {busyId === p.id ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} PDF
                      </button>
                      {!isPaid && (
                        <button onClick={() => markPaid(p)} disabled={busyId === p.id}
                                className="text-xs px-2 py-1 bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                                data-testid={`payout-mark-${p.id}`}>
                          <CheckCircle2 size={11} /> Markér betalt
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {payouts.length > 0 && (
        <div className="bg-white border border-brand">
          <div className="p-5 border-b border-brand flex items-center gap-2">
            <Banknote size={16} className="text-brand-red" />
            <div className="font-display font-bold text-brand-dark">Udbetalingshistorik</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-brand-bg text-brand-text2 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Partner</th>
                <th className="text-left p-3">Periode</th>
                <th className="text-right p-3">Beløb</th>
                <th className="text-left p-3">Reference</th>
                <th className="text-left p-3">Betalt</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((x) => (
                <tr key={x.id} className="border-t border-brand">
                  <td className="p-3 text-brand-dark">{x.shop_name}</td>
                  <td className="p-3">{x.period}</td>
                  <td className="p-3 text-right font-semibold">{fmtDkk(x.amount_dkk)}</td>
                  <td className="p-3 text-xs text-brand-text2 font-mono">{x.payout_ref || "—"}</td>
                  <td className="p-3 text-xs text-brand-text2">{(x.paid_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
